import { MutationFunctionOptions, useMutation, useQuery } from '@apollo/client';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import LoadingPage from '@website/components/utilities/LoadingPage';
import NotFound from '@website/components/utilities/NotFound';
import { LegacyTrip } from '@website/types/LegacyTrip';
import { ObjectNumbersToStrings } from '@website/types/ObjectNumbersToStrings';
import { Form, FormCheck } from 'react-bootstrap';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
  EditTripFormSettingsDocument,
  EditTripFormSettingsQuery,
  UpdateTripFormSettingsDocument,
  TripFormRequiredInformationInput,
  UpdateTripFormSettingsMutation,
} from '../../../types/graphqlOperations';

/**
 * Required information checkboxes
 *
 * Checkboxes for things like whether or not an emergency contact is needed on the customer trip form.
 */
export const requiredInformationCheckboxes: {
  [key: string]: {
    name: string;
    description: string;
  };
} = {
  emergencyContact: {
    name: 'Emergency contact person',
    description:
      'Contact name, phone number, email address and relationship to the guest.',
  },
  passportInformation: {
    name: 'Passport information',
    description:
      'Name as on the passport, passport number, nationality, date of birth, issue date, expiry date and gender.',
  },
  travelInformation: {
    name: 'Travel/flights details',
    description:
      'Transport method, flight numbers, arrival and departure time.',
  },
  travelInsurance: {
    name: 'Travel insurance details',
    description: 'Insurance company, policy number, emergency phone number.',
  },
};

export type EditTripFormValues = Omit<
  ObjectNumbersToStrings<TripFormRequiredInformationInput>, // HTML forms turn numbers into strings
  'tripID' // TripID is not editable in the form
>;

/**
 * Required information checkboxes - react components
 */
export function RequiredInformationCheckboxFormItems({
  register,
}: {
  register: UseFormReturn<EditTripFormValues>['register'];
}) {
  return (
    <>
      {Object.keys(requiredInformationCheckboxes).map(
        (fieldName: keyof EditTripFormValues) => (
          <Form.Check key={fieldName}>
            <FormCheck.Input {...register(fieldName)} />
            <FormCheck.Label>
              {requiredInformationCheckboxes[fieldName].name}
              <br />
              <small>
                {requiredInformationCheckboxes[fieldName].description}{' '}
              </small>
            </FormCheck.Label>
          </Form.Check>
        ),
      )}
    </>
  );
}

/**
 * Submit handler
 *
 * @param values
 * @param tripID
 * @param updateTripFormRequiredInformation Apollo mutation
 */
export async function tripFormRequiredInformationSubmitHandler(
  values: EditTripFormValues,
  tripID: string,
  updateTripFormRequiredInformation: (
    options: MutationFunctionOptions<
      UpdateTripFormSettingsMutation,
      { input: TripFormRequiredInformationInput }
    >,
  ) => Promise<any>,
) {
  // Convert any number fields to string
  const input: TripFormRequiredInformationInput = {
    additionalInformation: values.additionalInformation,
    daysBeforeTravelDue: parseInt(values.daysBeforeTravelDue),
    emergencyContact: values.emergencyContact,
    passportInformation: values.passportInformation,
    travelInformation: values.travelInformation,
    travelInsurance: values.travelInsurance,
    tripID,
  };

  try {
    await updateTripFormRequiredInformation({
      variables: { input },
    });
    alert('Updated successfully.');
  } catch (e) {
    alert(e);
  }
}

/**
 * Edit trip form settings form
 *
 * Allows the host to customise the trip form that their guests will be asked to fill in.
 */
export function EditTripFormSettingsForm({
  tripFormRequiredInformation,
}: {
  tripFormRequiredInformation: EditTripFormSettingsQuery['trip']['tripFormRequiredInformation'];
}) {
  const [updateTripFormRequiredInformation] = useMutation(
    UpdateTripFormSettingsDocument,
  );

  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
  } = useForm<EditTripFormValues>({
    defaultValues: {
      ...tripFormRequiredInformation,
      daysBeforeTravelDue:
        tripFormRequiredInformation.daysBeforeTravelDue.toString(),
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((values: EditTripFormValues) =>
        tripFormRequiredInformationSubmitHandler(
          values,
          tripFormRequiredInformation.tripID,
          updateTripFormRequiredInformation,
        ),
      )}
    >
      <Form.Group controlId="daysBeforeTravelDue">
        <Form.Label>
          How many days before the start of the trip, do you need the guest's
          trip form?
        </Form.Label>
        <Form.Control
          type="number"
          {...register('daysBeforeTravelDue')}
          min={0}
          max={60}
          step={1}
        />
        <Form.Text>
          Please choose 0 unless you have a very good reason. We'll still chase
          the customer regularly (starting two months before this point) to fill
          in the form, with emails and/or text messages.
        </Form.Text>
      </Form.Group>
      <Form.Group>
        <Form.Label>Do you need any additional information?</Form.Label>
        <Form.Text>
          You will always receive the guest name, phone number and email. If you
          need any additional information please select this below. Please only
          request information that you absolutely need - it's a long form and
          your guests will thank you for making it shorter!
        </Form.Text>
        <RequiredInformationCheckboxFormItems register={register} />
      </Form.Group>
      <Form.Group controlId="additionalInformation">
        <Form.Label>Is there anything else you need?</Form.Label>
        <Form.Text>
          You can ask anything else here - e.g. "Would you like to be picked up
          from location A or B?"
        </Form.Text>
        <Form.Control as="textarea" {...register('additionalInformation')} />
      </Form.Group>
      <br />
      <SubmitButton isSubmitting={isSubmitting} block />
    </Form>
  );
}

/**
 * Edit Trip Form Settings Tab
 */
export default function EditTripFormSettings({
  trip,
}: {
  trip: Pick<LegacyTrip, 'id'>;
}) {
  // Get the initial values
  const { data, loading, error } = useQuery(EditTripFormSettingsDocument, {
    variables: { tripID: trip.id },
    fetchPolicy: 'cache-and-network',
  });

  // Handle loading/error
  if (loading) return <LoadingPage />;
  if (error) return <NotFound />;

  // Return the form
  return (
    <EditTripFormSettingsForm
      tripFormRequiredInformation={data.trip.tripFormRequiredInformation}
    />
  );
}
